import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import { Modal, Form, Button, Radio, Input, Icon, notification } from 'tea-component';
import { useIntl } from 'react-intl';
import TinyEditor from '@sg/components/TinyEditor';
import UploadFileList from '@sg/components/UploadFileList';
import { getDashboardCardMetrics, getWeeklyCardMetrics, getMonthlyCardMetrics, getGameMetricList, getDashboardRainbowInfo } from '@/pages/strategic/services';
import MetricsBoardVertical from '@/pages/strategic/components/MetricsBoardVertical';
import ContentCard from '@sg/components/ContentCard';
import Loading from '@intelligence/components/Loading';
import chatGPTIcon from '@sg/assests/svg/chatgpt-icon.svg';
import deepSeekIcon from '@sg/assests/svg/deepseek-icon.svg';
import { ReactSortable } from 'react-sortablejs';
import { formatPercentage } from '@/pages/strategic/utils/common';
import { transformTrendData } from '@/pages/strategic/components/MetricsBoardVertical/transformTrendData';
import useCos from '@gp/hooks/useCos';
import { getBIDSTencentQCouldMassage } from '@sg/services/cos';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import { getMetricDisplayName, findMetricInfo, getComparisonLabels, processRainbowInfo, getDateRangeByGranularity, type MetricTextInfo } from '../../utils/metricUtils';
import {
  ContentData,
  SelectedProject,
  Project
} from '../../types';
import {
  filterMetricsByDataFrame,
  METRIC_SPECIAL_COMPARISON_LABELS,
  METRIC_NO_ACCESS_CODE,
  METRIC_NO_ACCESS_STATUS,
  type DataFrameType,
} from '../../config';
import { useContentForm } from './hooks/useContentForm';
import './index.scss';

interface AddNewContentModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (result: {
    type: 'new-project' | 'add-content' | 'edit-content';
    projectId?: string; // temp ID for new project, real ID for existing
    contentItems?: Array<{ id: string; type: string; data: any }>; // temp IDs for new content
    contentOrders?: string; // JSON string of content IDs (may include temp IDs)
    editedContent?: { id: string; type: string; data: any }; // for editing existing content
    // Pass already-fetched data to avoid duplicate API calls
    metricTextInfo?: MetricTextInfo[]; // Rainbow info for titles
    metricsData?: { card_list: any[]; trend_list: any[] }; // Metrics data (card_list and trend_list)
    gameCode?: string; // Game code for the metrics data
  }) => void;
  mode: 'new' | 'existing'; // Mode to distinguish between flows
  selectedProject: SelectedProject | null;
  existingProjectData?: { // Only the specific fields we need for existing project
    id: number;
    game_code: string;
    project_name: string;
    order: number;
    content_orders: string;
  } | null;
  projects?: Project[]; // For finding project by content item and getting game_code
  editingContent?: { // Content data for editing
    id: string;
    type: string;
    data: any;
  } | null;
  generateTempId: (type: 'project' | 'content') => string; // Function to generate temp IDs
}

const AddNewContentModal: React.FC<AddNewContentModalProps> = ({
  visible,
  onClose,
  onConfirm,
  mode,
  selectedProject,
  existingProjectData,
  projects,
  editingContent,
  generateTempId,
}) => {
  const { formatMessage, locale } = useIntl();
  const [contentType, setContentType] = useState<'dashboard' | 'ai-summary' | 'text'>('dashboard');
  
  // Use content form hook
  const {
    textType,
    setTextType,
    textTitle,
    setTextTitle,
    textContent,
    setTextContent,
    textFiles,
    setTextFiles,
    agent,
    setAgent,
    aiPrompt,
    setAiPrompt,
    aiFiles,
    setAiFiles,
    isTextFilesReady,
    isAiFilesReady,
    recommendedCharacters,
    previewFiles,
    memoizedTextFiles,
    memoizedAiFiles,
    handleTextContentChange,
    handleTextFilesChange,
    handleAiFilesChange,
    handleTextFilesIsAllow,
    handleAiFilesIsAllow,
    resetFormFields: resetContentFormFields,
  } = useContentForm();
  
  // State for selected metrics - NewRegionSelect expects specific format
  const [selectedDashboardValue, setSelectedDashboardValue] = useState<any>({
    type: 'market',
    regions: [],
    markets: [],
  });
  
  // Derive simple array from NewRegionSelect value for backward compatibility
  const selectedDashboardData = useMemo(() => {
    return selectedDashboardValue?.markets || [];
  }, [selectedDashboardValue]);
  
  // State for API-fetched metrics (now includes hierarchical structure from API)
  const [loadingMetricsList, setLoadingMetricsList] = useState(false);
  // metricsHierarchy stores the hierarchical structure of metrics grouped by time granularity (daily, weekly, monthly) as returned by the API
  // [
  //   {
  //     granularity: "daily",
  //     metrics: ["active_users", "above_2hr_users", "mtx_revenue"]
  //   },
  //   {
  //     granularity: "weekly", 
  //     metrics: ["active_users", "above_2hr_users", "new_users"]
  //   },
  //   {
  //     granularity: "monthly",
  //     metrics: ["active_users", "mtx_revenue"]
  //   }
  // ]

  const [metricsHierarchy, setMetricsHierarchy] = useState<Array<{
    granularity: string;
    metrics: string[];
  }>>([]);
  const [metricTextInfo, setMetricTextInfo] = useState<Array<{
    value: string;
    text_en: string;
    text_cn: string;
    formatter?: string;
    unit?: string; // 'hour' | 'USD' | 'RMB'
    granularity?: string; // Track which granularity this text info is for (for active_users variants)
  }>>([]);

  // Transform API response to hierarchical format for NewRegionSelect
  const dashboardHierarchicalOptions = useMemo(() => {
    const isZh = locale === 'zh-CN';
    
    // Get data frame type from selectedProject (default to 'mobile' if not specified)
    const dataFrame: DataFrameType = (selectedProject?.type as DataFrameType) || 'mobile';
    
    // Granularity display names mapping
    const granularityNames: { [key: string]: { en: string; cn: string; displayEn: string; displayCn: string } } = {
      'daily': { en: 'Key Metrics', cn: '关键指标', displayEn: 'Daily', displayCn: '每日' },
      'weekly': { en: 'Key Metrics', cn: '关键指标', displayEn: 'Weekly', displayCn: '每周' },
      'monthly': { en: 'Key Metrics', cn: '关键指标', displayEn: 'Monthly', displayCn: '每月' },
    };
    
    // Build hierarchical options from API response
    const hierarchicalOptions: any[] = [];
    
    metricsHierarchy.forEach((group) => {
      const { granularity, metrics } = group;
      const displayName = granularityNames[granularity] || { en: granularity, cn: granularity, displayEn: granularity, displayCn: granularity };
      
      // Filter metrics based on data frame configuration (PC or Mobile)
      const filteredMetrics = filterMetricsByDataFrame(
        metrics, 
        dataFrame, 
        granularity as 'daily' | 'weekly' | 'monthly'
      );
      
      if (filteredMetrics && filteredMetrics.length > 0) {
        hierarchicalOptions.push({
          text: displayName.en, // Right panel title: "Key Metrics"
          textCN: displayName.cn, // Right panel title: "关键指标"
          value: granularity, // daily/weekly/monthly
          // Custom render for left panel to show granularity
          render: (
            <span>
              {/* {isZh ? displayName.displayCn : displayName.displayEn} */}
              {/* temporary disable chines ename */}
              {displayName.displayEn} 
            </span>
          ),
          children: filteredMetrics
            .map((metricKey) => {
              // Find metric info using shared utility
              const metricInfo = findMetricInfo(metricKey, metricTextInfo, granularity);
              // IMPORTANT: Use unique value format "granularity__metricKey" to distinguish same metrics across different granularities
              // e.g., "daily__above_2hr_users" vs "weekly__above_2hr_users" vs "monthly__above_2hr_users"
              return {
                text: metricInfo?.text_en || metricKey,
                textCN: metricInfo?.text_cn || metricKey,
                value: `${granularity}__${metricKey}`, // Unique identifier combining granularity and metric key
              };
            })
            .sort((a, b) => {
              // Sort by text_en in ascending order (numbers 0-9 first, then letters a-z)
              const textA = (a.text || '').toLowerCase();
              const textB = (b.text || '').toLowerCase();
              return textA.localeCompare(textB, 'en', { numeric: true });
            }),
        });
      }
    });
    
    return hierarchicalOptions;
  }, [metricsHierarchy, metricTextInfo, locale, selectedProject?.type]);

  // Button element for NewRegionSelect
  const dashboardSelectButton = useMemo(() => {
    const key = locale === 'zh-CN' ? 'cn' : 'en';
    
    // Ensure selectedDashboardData is an array
    const dataArray = Array.isArray(selectedDashboardData) ? selectedDashboardData : [];
    const count = dataArray.length;
    const totalCount = metricTextInfo.length;
    
    let displayText = '';
    if (count === 0) {
      displayText = formatMessage({ id: 'None' });
    } else if (count === totalCount && totalCount > 0) {
      displayText = formatMessage({ id: 'All' });
    } else if (count > 3) {
      displayText = `${count}`;
    } else {
      // Show up to 2 metric names
      displayText = dataArray
        .slice(0, 2)
        .map((combinedValue) => {
          // Parse combined value: "daily__active_users" -> ["daily", "active_users"]
          const [granularity, metricKey] = combinedValue.split('__');
          
          // Use shared utility to find metric with granularity matching
          const metric = findMetricInfo(metricKey, metricTextInfo, granularity);
          // return locale === 'zh-CN' ? metric?.text_cn : metric?.text_en;
          return metric?.text_en; // temporary disable chinese name
        })
        .filter(Boolean)
        .join(', ');
    }
    
    return (
      <div>
        {/* <span className="dbd-filter-content-title-item-name">
          {formatMessage({ id: 'dbs_dashboard' })}
        </span> */}
        {displayText}
      </div>
    );
  }, [selectedDashboardData, metricTextInfo, locale, formatMessage]);

  // Handle selection change for NewRegionSelect
  const handleDashboardSelectionChange = useCallback((value: any) => {
    console.log('Dashboard selection changed:', value);
    setSelectedDashboardValue(value);
  }, []);

  // Track if we're editing existing content
  const isEditing = !!editingContent;

  // Pre-populate form when editing
  useEffect(() => {
    if (editingContent) {
      setContentType(editingContent.type as 'dashboard' | 'ai-summary' | 'text');

      // Pre-populate based on content type
      if (editingContent.type === 'dashboard' && editingContent.data?.metrics) {
        // IMPORTANT: When editing, convert saved data back to "granularity__metricKey" format
        // We now store granularity separately in the data for accurate reconstruction
        const convertedMetricNames = editingContent.data.metrics.map((metricItem: any) => {
          const metricKey = metricItem.metrics_name;
          const granularity = metricItem.granularity;
          
          // If granularity is stored in the data, use it directly (new format)
          if (granularity) {
            return `${granularity}__${metricKey}`;
          }
          
          // Fallback: Find which granularity this metric belongs to (backward compatibility for old data)
          // Note: This will use the FIRST occurrence if metric appears in multiple granularities
          const granularityGroup = metricsHierarchy.find(group => 
            group.metrics.includes(metricKey)
          );
          
          if (granularityGroup) {
            return `${granularityGroup.granularity}__${metricKey}`;
          }
          
          // Fallback: if not found in hierarchy, return original key
          console.warn(`Metric ${metricKey} not found in metricsHierarchy during editing`);
          return metricKey;
        });
        
        setSelectedDashboardValue({
          type: 'market',
          regions: [],
          markets: convertedMetricNames,
        });
      } else if (editingContent.type === 'text' && editingContent.data) {
        setTextType(editingContent.data.textType || editingContent.data.type || 'long');
        setTextTitle(editingContent.data.textTitle || '');
        setTextContent(editingContent.data.textContent || '');
        setTextFiles(editingContent.data.documents || []);
        console.log('Pre-populating text files for editing:', editingContent.data.documents);
      } else if (editingContent.type === 'ai-summary' && editingContent.data) {
        setAiPrompt(editingContent.data.prompt || '');
        setAgent(editingContent.data.agent || 'chatGPT');
        setAiFiles(editingContent.data.documents || []);
        console.log('Pre-populating AI files for editing:', editingContent.data.documents);
      }
    }
  }, [editingContent, metricsHierarchy]);

  // Fetch metrics list from API when modal opens and game_code is available
  useEffect(() => {
    const fetchMetricsList = async () => {
      // Only fetch when modal is visible and we have a game_code
      if (!visible || !selectedProject?.value) {
        return;
      }

      try {
        setLoadingMetricsList(true);
        
        // Step 1: Fetch hierarchical metrics list from new API v2
        const metricsResponse = await getGameMetricList({ game_code: selectedProject.value });
        
        if (metricsResponse?.data?.metrics && Array.isArray(metricsResponse.data.metrics)) {
          const metricsHierarchyData = metricsResponse.data.metrics;
          // console.log('Fetched hierarchical metrics from API:', metricsHierarchyData);
          
          // Store the hierarchical structure
          setMetricsHierarchy(metricsHierarchyData);
          
          // Step 2: Fetch metric text info (text_en and text_cn) using rainbow info API
          try {
            // Determine game_code for rainbow info based on data_frame
            // If data_frame is 'pc', use 'pc_demo'; if 'mobile', use 'demo' (as instructed by 经分前端 因为七彩石 没有所有项目的指标都配， 所以只配了pc_demo和demo)
            let rainbowGameCode = selectedProject.value; // Default to selected game code
            if (selectedProject.type === 'pc') {
              rainbowGameCode = 'pc_demo';
            } else if (selectedProject.type === 'mobile') {
              rainbowGameCode = 'demo';
            }
            
            // console.log(`🎮 Using rainbow info game_code: ${rainbowGameCode} (data_frame: ${selectedProject.type})`);
            
            const textInfoResponse = await getDashboardRainbowInfo({ 
              game_code: rainbowGameCode,
              folder: 'metric_info'
            });
            
            // Convert hierarchy data to metricsByGranularity format for shared utility
            const metricsByGranularity: Record<string, string[]> = {};
            metricsHierarchyData.forEach((group: { granularity: string; metrics: string[] }) => {
              if (group.metrics && Array.isArray(group.metrics)) {
                metricsByGranularity[group.granularity] = group.metrics;
              }
            });
            
            // Use shared utility to process Rainbow Info
            const processedTextInfo = processRainbowInfo(textInfoResponse, metricsByGranularity);
            
            console.log('Processed text info:', processedTextInfo);
            setMetricTextInfo(processedTextInfo);
          } catch (textError: any) {
            console.error('Error fetching metric text info:', textError);
            notification.error({
              description: textError?.response?.data?.message || textError?.message || 'Failed to fetch metric text info',
            });
            setMetricTextInfo([]);
          }
        } else {
          console.warn('Unexpected API response format:', metricsResponse);
          notification.warning({
            description: 'Unexpected API response format from getGameMetricList',
          });
          setMetricTextInfo([]);
        }
      } catch (error: any) {
        notification.error({
          description: error?.response?.data?.message || error?.message || 'Failed to fetch metrics list',
        });
        setMetricTextInfo([]);
      } finally {
        setLoadingMetricsList(false);
      }
    };

    fetchMetricsList();
  }, [visible, selectedProject?.value]);

  // Dashboard metrics data
  const [dashboardMetrics, setDashboardMetrics] = useState<any[]>([]);
  const [trendList, setTrendList] = useState<any[]>([]); // Store trend_list from API
  const [loadingMetrics, setLoadingMetrics] = useState<boolean>(false);
  const [isUploadAllowed, setIsUploadAllowed] = useState<boolean>(true);
  const [deletePathList, setDeletePathList] = useState<string[]>([]);

  // COS delete object hook
  const { deleteObject } = useCos({ 
    qCouldService: getBIDSTencentQCouldMassage,
    qCouldServiceParams: 'weekly_project_highlight',
  });

  // Delete files from cloud storage
  const deleteFileFun = async () => {
    const deFunList = deletePathList?.map((path: string) => {
      return deleteObject({ path });
    });
    Promise.all(deFunList).then(() => { });
  };

  const createdTime = moment().format('YYYY-MM-DD'); // Current date

  const isMetricNoAccessError = (error: any): boolean => {
    const responseData = error?.response?.data || error?.data || {};
    const statusCode = Number(error?.response?.status ?? error?.status);
    const responseCode = Number(responseData?.code ?? error?.code);
    return (
      statusCode === METRIC_NO_ACCESS_STATUS ||
      responseCode === METRIC_NO_ACCESS_CODE
    );
  };

  const buildNoAccessMetricCards = (
    metrics: string[],
    granularity: string
  ): any[] => {
    return metrics.map((metricName) => ({
      metrics_name: metricName,
      granularity,
      noAccess: true,
      value: null,
    }));
  };

  const buildNoAccessCardsForGranularityGroups = (
    granularityGroups: Record<string, string[]>
  ): any[] => {
    const cardList: any[] = [];
    Object.entries(granularityGroups).forEach(([granularity, metrics]) => {
      cardList.push(...buildNoAccessMetricCards(metrics, granularity));
    });
    return cardList;
  };

  // Fetch dashboard metrics data
  const fetchDashboardMetrics = useCallback(async () => {
    if (!selectedProject || !Array.isArray(selectedDashboardData) || selectedDashboardData.length === 0) return;

    // Group selected metrics by their granularity
    // selectedDashboardData now contains values in format: "granularity__metricKey"
    const metricsByGranularity: { [key: string]: string[] } = {};

    selectedDashboardData.forEach((combinedValue: string) => {
      // Parse the combined value: "daily__above_2hr_users" -> ["daily", "above_2hr_users"]
      const [granularity, metricKey] = combinedValue.split('__');

      if (granularity && metricKey) {
        if (!metricsByGranularity[granularity]) {
          metricsByGranularity[granularity] = [];
        }
        metricsByGranularity[granularity].push(metricKey);
      }
    });

    try {
      setLoadingMetrics(true);

      // Prepare API calls for each granularity using shared date calculation utilities
      const apiCalls = Object.keys(metricsByGranularity).map(async (granularity) => {
        const metrics = metricsByGranularity[granularity];
        let requestData: any;
        let apiFunction: any;

        // Get date range using shared utility
        const dateRange = getDateRangeByGranularity(granularity as 'daily' | 'weekly' | 'monthly');

        if (granularity === 'daily') {
          // for daily, we will spcial handle full_game_asp metrics as it need additional params when calling api
          // Check if full_game_asp is in the metrics array
          const hasFullGameAsp = metrics.includes('full_game_asp');
          const otherMetrics = metrics.filter((m: string) => m !== 'full_game_asp');
          
          // If full_game_asp exists, make separate API calls
          if (hasFullGameAsp) {
            const results: any[] = [];
            
            // API call for full_game_asp with is_confirm: true
            const aspRequestData = {
              game_code: selectedProject.value,
              period: 'daily',
              start_time: dateRange.start_time,
              end_time: dateRange.end_time,
              metrics: ['full_game_asp'],
              trend_metrics: ['full_game_asp'],
              is_confirm: true
            };
            const aspResponse = await getDashboardCardMetrics(aspRequestData);
            results.push(aspResponse?.data || { card_list: [], trend_list: [] });
            
            // API call for other metrics without is_confirm (if any)
            if (otherMetrics.length > 0) {
              const othersRequestData = {
                game_code: selectedProject.value,
                period: 'daily',
                start_time: dateRange.start_time,
                end_time: dateRange.end_time,
                metrics: otherMetrics,
                trend_metrics: otherMetrics
              };
              const othersResponse = await getDashboardCardMetrics(othersRequestData);
              results.push(othersResponse?.data || { card_list: [], trend_list: [] });
            }
            
            // Merge the results
            // For card_list: simple concatenation is fine
            const mergedCardList = results.flatMap(r => r.card_list || []);
            
            // For trend_list: merge by date to avoid duplicate date entries
            const trendMap = new Map();
            results.forEach(result => {
              (result.trend_list || []).forEach((trendItem: any) => {
                const key = trendItem.date;
                if (trendMap.has(key)) {
                  // Merge metrics objects for the same date
                  const existing = trendMap.get(key);
                  trendMap.set(key, {
                    ...existing,
                    metrics: {
                      ...existing.metrics,
                      ...trendItem.metrics
                    }
                  });
                } else {
                  trendMap.set(key, trendItem);
                }
              });
            });
            const mergedTrendList = Array.from(trendMap.values());
            
            const mergedData = {
              card_list: mergedCardList,
              trend_list: mergedTrendList
            };
            
            return { 
              granularity, 
              data: mergedData
            };
          } else {
            // No full_game_asp, proceed normally
            requestData = {
              game_code: selectedProject.value,
              period: 'daily',
              start_time: dateRange.start_time,
              end_time: dateRange.end_time,
              metrics,
              trend_metrics: metrics
            };
            apiFunction = getDashboardCardMetrics;
          }

        } else if (granularity === 'weekly') {
          requestData = {
            game_code: selectedProject.value,
            period: 'weekly' as 'weekly',
            start_time: dateRange.start_time,
            end_time: dateRange.end_time,
            metrics,
            trend_metrics: metrics
          };
          apiFunction = getWeeklyCardMetrics;

        } else if (granularity === 'monthly') {
          requestData = {
            game_code: selectedProject.value,
            period: 'monthly' as 'monthly',
            start_time: dateRange.start_time,
            end_time: dateRange.end_time,
            metrics,
            trend_metrics: metrics
          };
          apiFunction = getMonthlyCardMetrics;
        }

        // Call the appropriate API (if not already handled for daily with full_game_asp)
        if (apiFunction) {
          const response = await apiFunction(requestData);
          // Attach granularity to the response for later use
          return { 
            granularity, 
            data: response?.data || { card_list: [], trend_list: [] } 
          };
        }
      });

      // Execute all API calls in parallel
      const results = await Promise.all(apiCalls);

      // Merge all card_list and trend_list results, attaching granularity to each metric
      const allCardList: any[] = [];
      const allTrendList: any[] = [];

      results.forEach(result => {
        if (!result) {
          return;
        }
        const { granularity, data } = result;
        
        if (data.card_list) {
          // Attach granularity to each metric in card_list
          const metricsWithGranularity = data.card_list.map((metric: any) => ({
            ...metric,
            granularity, // Add granularity field to each metric
          }));
          allCardList.push(...metricsWithGranularity);
        }
        
        if (data.trend_list) {
          // IMPORTANT: Attach granularity to each trend item for proper filtering later
          const trendListWithGranularity = data.trend_list.map((trend: any) => ({
            ...trend,
            granularity, // Add granularity field to each trend item
          }));
          allTrendList.push(...trendListWithGranularity);
        }
      });

      console.log('Merged card_list with granularity:', allCardList);
      console.log('Merged trend_list:', allTrendList);

      setDashboardMetrics(allCardList);
      setTrendList(allTrendList);

    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      if (isMetricNoAccessError(error)) {
        setDashboardMetrics(
          buildNoAccessCardsForGranularityGroups(metricsByGranularity)
        );
        setTrendList([]);
        return;
      }
      notification.error({
        description: 'Failed to fetch dashboard metrics',
      });
    } finally {
      setLoadingMetrics(false);
    }
  }, [selectedProject?.value, selectedDashboardData, metricsHierarchy]);

  // Fetch metrics when selectedDashboardData changes
  useEffect(() => {
    const dataLength = Array.isArray(selectedDashboardData) ? selectedDashboardData.length : 0;
    
    if (contentType === 'dashboard' && dataLength > 0 && selectedProject?.value) {
      fetchDashboardMetrics();
    } else if (contentType === 'dashboard' && dataLength === 0) {
      // Clear preview when dropdown is empty
      setDashboardMetrics([]);
      setTrendList([]);
    } else if (contentType !== 'dashboard') {
      // Clear preview when switching away from dashboard
      setDashboardMetrics([]);
      setTrendList([]);
    }
  }, [contentType, selectedDashboardData, selectedProject?.value, fetchDashboardMetrics]);

  const handleConfirm = async () => {
    try {
      let contentData: ContentData | null = null;
      let hasContentData = false;

      // Build content data based on type
      if (contentType === 'text') {
        // When editing, always include file data even if title/content haven't changed
        // For new content, allow saving if either title or content has content (or both)
        if (isEditing || textTitle.trim() || textContent.trim() || textFiles.length > 0) {
          hasContentData = true;
          contentData = {
            textType,
            textTitle,
            textContent,
            documents: textFiles
          };
          console.log('Building text contentData:', contentData);
        }
      } else if (contentType === 'dashboard') {
        // Check if dashboard metrics exist
        if (dashboardMetrics.length > 0) {
          hasContentData = true;
          // Use the first metric's data for the main contentData (for compatibility)
          contentData = {
            metrics: [{
              metrics_name: dashboardMetrics[0].metrics_name,
              granularity: dashboardMetrics[0].granularity  // Include granularity
            }]
          };
        }
      } else {
        // AI Summary
        // When editing, always include file data even if prompt hasn't changed
        // For new content, require prompt
        if (isEditing || aiPrompt.trim()) {
          hasContentData = true;
          contentData = {
            prompt: aiPrompt,
            agent,
            documents: aiFiles
          };
          console.log('Building AI summary contentData:', contentData);
        }
      }
      console.log('contentData to submit', contentData);
      console.log('hasContentData', hasContentData);

      // Handle editing existing content
      if (isEditing && editingContent) {
        console.log('Editing content with data:', contentData);
        console.log('Content type:', contentType);

        if (contentType === 'dashboard' && dashboardMetrics.length > 0) {
          // Special handling for dashboard: 1 metric = update, multiple metrics = update first + create rest with temp IDs
          const contentItems: Array<{ id: string; type: string; data: any }> = [];
          
          if (dashboardMetrics.length === 1) {
            // Single metric: just update the existing content item
            const metricContentData = {
              metrics: [{
                metrics_name: dashboardMetrics[0].metrics_name,
                granularity: dashboardMetrics[0].granularity  // Include granularity
              }]
            };
            
            contentItems.push({
              id: editingContent.id, // Keep existing ID
              type: contentType,
              data: metricContentData
            });
          } else {
            // Multiple metrics: update first item, create rest with temp IDs
            // Update the first content item with the first metric
            const firstMetricContentData = {
              metrics: [{
                metrics_name: dashboardMetrics[0].metrics_name,
                granularity: dashboardMetrics[0].granularity  // Include granularity
              }]
            };
            
            contentItems.push({
              id: editingContent.id, // Keep existing ID for update
              type: contentType,
              data: firstMetricContentData
            });
            
            // Generate temp IDs for remaining metrics
            for (let i = 1; i < dashboardMetrics.length; i++) {
              const metricContentData = {
                metrics: [{
                  metrics_name: dashboardMetrics[i].metrics_name,
                  granularity: dashboardMetrics[i].granularity  // Include granularity
                }]
              };

              const tempContentId = generateTempId('content');
              contentItems.push({
                id: tempContentId,
                type: contentType,
                data: metricContentData
              });
            }
          }
          
          // Calculate updated content_orders
          let updatedContentOrders: (string | number)[] = [];
          if (existingProjectData) {
            const existingContentOrders = JSON.parse(existingProjectData.content_orders || '[]');
            const existingContentId = parseInt(editingContent.id);
            const existingIndex = existingContentOrders.indexOf(existingContentId);
            
            if (existingIndex !== -1) {
              // Replace the existing content ID with the new content IDs
              const newContentIds = contentItems.map(item => {
                // Keep existing ID as number, new temp IDs as strings
                return item.id === editingContent.id ? parseInt(item.id) : item.id;
              });
              updatedContentOrders = [
                ...existingContentOrders.slice(0, existingIndex),
                ...newContentIds,
                ...existingContentOrders.slice(existingIndex + 1)
              ];
            } else {
              // If not found, just append
              updatedContentOrders = [...existingContentOrders, ...contentItems.map(item => 
                item.id === editingContent.id ? parseInt(item.id) : item.id
              )];
            }
          }

          // Call parent's onConfirm with edit result
          if (onConfirm) {
            // Find the project to get game_code
            const project = projects?.find(p => {
              return p.content_items.some(item => item.id === editingContent.id);
            });
            
            await onConfirm({
              type: 'edit-content',
              contentItems,
              contentOrders: JSON.stringify(updatedContentOrders),
              editedContent: {
                id: editingContent.id,
                type: contentType,
                data: contentData
              },
              // Pass already-fetched data for dashboard content to avoid duplicate API calls
              ...(contentType === 'dashboard' && project?.game_code ? {
                metricTextInfo,
                metricsData: {
                  card_list: dashboardMetrics,
                  trend_list: trendList
                },
                gameCode: project.game_code
              } : {})
            });
          }
        } else {
          // For non-dashboard content types, just update normally
          if (onConfirm) {
            // Find the project to get game_code (for consistency, though not needed for non-dashboard)
            const project = projects?.find(p => {
              return p.content_items.some(item => item.id === editingContent.id);
            });
            
            await onConfirm({
              type: 'edit-content',
              editedContent: {
                id: editingContent.id,
                type: contentType,
                data: contentData
              },
              // Only pass data if it's dashboard content
              ...(contentType === 'dashboard' && project?.game_code ? {
                metricTextInfo,
                metricsData: {
                  card_list: dashboardMetrics,
                  trend_list: trendList
                },
                gameCode: project.game_code
              } : {})
            });
          }
        }
      } else if (mode === 'new') {
        // Creating new project with content
        const contentItems: Array<{ id: string; type: string; data: any }> = [];
        const tempContentIds: string[] = [];

        if (hasContentData) {
          if (contentType === 'dashboard' && dashboardMetrics.length > 0) {
            // Generate temp ID for each metric
            for (const metric of dashboardMetrics) {
              const metricContentData = {
                metrics: [{
                  metrics_name: metric.metrics_name,
                  granularity: metric.granularity  // Store granularity separately
                }]
              };
              const tempContentId = generateTempId('content');
              tempContentIds.push(tempContentId);
              contentItems.push({
                id: tempContentId,
                type: contentType,
                data: metricContentData
              });
            }
          } else if (contentData) {
            // For other content types, create single content item with temp ID
            const tempContentId = generateTempId('content');
            tempContentIds.push(tempContentId);
            contentItems.push({
              id: tempContentId,
              type: contentType,
              data: contentData
            });
          }
        }

        // Generate temp project ID
        const tempProjectId = generateTempId('project');

        // Call parent's onConfirm with new project result
        if (onConfirm) {
          await onConfirm({
            type: 'new-project',
            projectId: tempProjectId,
            contentItems,
            contentOrders: JSON.stringify(tempContentIds),
            // Pass already-fetched data for dashboard content to avoid duplicate API calls
            ...(contentType === 'dashboard' && selectedProject?.value ? {
              metricTextInfo,
              metricsData: {
                card_list: dashboardMetrics,
                trend_list: trendList
              },
              gameCode: selectedProject.value
            } : {})
          });
        }
      } else if (mode === 'existing' && existingProjectData) {
        // Adding content to existing project
        const contentItems: Array<{ id: string; type: string; data: any }> = [];
        const tempContentIds: string[] = [];

        if (hasContentData) {
          if (contentType === 'dashboard' && dashboardMetrics.length > 0) {
            // Generate temp ID for each metric
            for (const metric of dashboardMetrics) {
              const metricContentData = {
                metrics: [{
                  metrics_name: metric.metrics_name,
                  granularity: metric.granularity  // Store granularity separately
                }]
              };
              const tempContentId = generateTempId('content');
              tempContentIds.push(tempContentId);
              contentItems.push({
                id: tempContentId,
                type: contentType,
                data: metricContentData
              });
            }
          } else if (contentData) {
            // For other content types, create single content item with temp ID
            const tempContentId = generateTempId('content');
            tempContentIds.push(tempContentId);
            contentItems.push({
              id: tempContentId,
              type: contentType,
              data: contentData
            });
          }
        }

        // Update existing project by appending new content to content_orders
        const existingContentOrders = JSON.parse(existingProjectData.content_orders || '[]');
        const updatedContentOrders = [...existingContentOrders, ...tempContentIds];

        // Call parent's onConfirm with add-content result
        if (onConfirm) {
          await onConfirm({
            type: 'add-content',
            projectId: String(existingProjectData.id),
            contentItems,
            contentOrders: JSON.stringify(updatedContentOrders),
            // Pass already-fetched data for dashboard content to avoid duplicate API calls
            ...(contentType === 'dashboard' && existingProjectData?.game_code ? {
              metricTextInfo,
              metricsData: {
                card_list: dashboardMetrics,
                trend_list: trendList
              },
              gameCode: existingProjectData.game_code
            } : {})
          });
        }
      }

      // Delete files from cloud storage if any were marked for deletion
      if (Array.isArray(deletePathList) && deletePathList.length > 0) {
        await deleteFileFun();
      }

      // Reset form fields after successful creation
      resetFormFields();
      onClose();
    } catch (error) {
      console.error('Error creating content:', error);
      // Show error message
      notification.error({
        description: 'Operation Failure',
      });
    }
  };

  // Reset all form fields to default values (dashboard-specific fields)
  const resetFormFields = () => {
    setDashboardMetrics([]);
    setContentType('dashboard');
    setSelectedDashboardValue({
      type: 'market',
      regions: [],
      markets: [],
    });
    setDeletePathList([]);
    setIsUploadAllowed(true);
    // Reset content form fields from hook
    resetContentFormFields();
  };

  const handleClose = async () => {
    // Delete all uploaded files when cancelling (since they're already in cloud)
    // This prevents orphaned files in cloud storage
    const allFiles = [...textFiles, ...aiFiles];
    if (allFiles.length > 0) {
      const allFilePaths = allFiles.map(file => file.path);
      const deletePromises = allFilePaths.map((path: string) => deleteObject({ path }));
      await Promise.all(deletePromises).catch((error) => {
        console.error('Error deleting files on cancel:', error);
      });
    }

    resetFormFields();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      caption={`${isEditing ? 'Edit' : 'Add New'} Content_${selectedProject?.text || 'Unknown Project'}`}
      size="xl"
      className="dbs-add-content-modal dbs-common-modal"
    >
      <Modal.Body>
        <Form>
          <Form.Item label={formatMessage({ id: 'dbs_type' })}  className="dbs-form-item-type-choice">
            <Radio.Group
              value={contentType}
              onChange={(value) => {
                setContentType(value as 'dashboard' | 'ai-summary' | 'text');
                setSelectedDashboardValue({ type: 'market', regions: [], markets: [] }); // Reset selection when type changes
              }}
            >
              <Radio name="dashboard">
                {formatMessage({ id: 'dbs_dashboard' })}
              </Radio>
              <Radio name="ai-summary">
                {formatMessage({ id: 'dbs_ai_summary' })}
              </Radio>
              <Radio name="text">
                {formatMessage({ id: 'dbs_text' })}
              </Radio>
            </Radio.Group>
          </Form.Item>

          {/* Dashboard section */}
          {contentType === 'dashboard' && (
            <>
              <Form.Item>
                 <div className='dbs-dashboard-select-wrapper'>
                  <NewRegionSelect
                    locale={locale}
                    // radioOptions={[]}
                    realRegionsList={[]}
                    value={selectedDashboardValue}
                    options={dashboardHierarchicalOptions}
                    loading={loadingMetricsList}
                    language={{
                      en: {
                        market_title: 'Dashboard',
                        region_title: 'Overview',
                        market_text: 'Key Metrics',
                        region_text: 'Overview',
                      },
                      cn: {
                        market_title: '仪表板',
                        region_title: '概览',
                        market_text: '关键指标',
                        region_text: '概览',
                      },
                    }}
                    button={dashboardSelectButton}
                    closeOnScroll={true}
                    boxClassName="dbs-dashboard-dropdown-wrapper dbs-common-box"
                    onCloseReginSelect={handleDashboardSelectionChange}
                    shortcutOptions={[]}
                  />
                </div>
              </Form.Item>

              <Form.Item>
                <div className="dbs-preview-wrapper">
                  {loadingMetrics ? (
                    <Loading classNameValue="dbs-loading" />
                  ) : dashboardMetrics.length > 0 ? (
                    <ReactSortable
                      list={dashboardMetrics}
                      setList={setDashboardMetrics}
                      animation={200}
                      handle=".dbs-metrics-drag-handle"
                      className="dbs-metrics-sortable"
                    >
                      {dashboardMetrics.map((metric, index) => {
                        // IMPORTANT: Filter trend_list by granularity to prevent mixing daily/weekly/monthly data
                        const metricGranularity = metric.granularity || 'daily'; // Default to daily for backward compatibility
                        const filteredTrendList = trendList.filter((trend: any) => 
                          trend.granularity === metricGranularity
                        );

                        // Transform trend data for this specific metric (using filtered trend list)
                        // Map granularity to dateDimension: 'daily' → 'DAY', 'weekly' → 'WEEK', 'monthly' → 'MONTH'
                        const dateDimension = metricGranularity === 'daily' 
                          ? 'DAY' 
                          : metricGranularity === 'weekly' 
                            ? 'WEEK' 
                            : 'MONTH';
                        
                        const { xAxisData, series } = transformTrendData(
                          filteredTrendList,
                          metric.metrics_name,
                          dateDimension,
                          formatMessage
                        );

                        // Find the formatter and unit for this metric using shared utility
                        const metricInfo = findMetricInfo(metric.metrics_name, metricTextInfo, metric.granularity);
                        const formatter = metricInfo?.formatter;
                        const unit = metricInfo?.unit;

                        // Determine textList labels based on granularity using shared utility
                        // Special metrics like full_game_asp use 'change' value instead of dod/sdlw
                        const usesChangeValue = metric.metrics_name in METRIC_SPECIAL_COMPARISON_LABELS;
                        const changeValue = usesChangeValue 
                            ? (metric as any).change?.show_value ?? null
                            : metric.dod?.show_value ?? null;
                        
                        const textList = getComparisonLabels(
                          metric.granularity || 'daily',
                          changeValue,
                          metric.sdlw?.show_value ?? null,
                          formatPercentage,
                          metric.metrics_name // Pass metric key for special handling
                        );

                        return (
                          <MetricsBoardVertical
                            key={index}
                            title={getMetricDisplayName(metric.metrics_name, locale, metricTextInfo, metric.granularity)}
                            num={metric.value !== undefined && metric.value !== null 
                              ? (typeof metric.value === 'number' 
                                  ? metric.value 
                                  : (isNaN(parseFloat(metric.value)) ? null : parseFloat(metric.value)))
                              : null}
                            formatter={formatter}
                            unit={unit}
                            mode="preview"
                            // style={{ minWidth: '140px' }}
                            textList={textList}
                            trendData={{
                              xAxisData,
                              series,
                              metrics_name: metric.metrics_name,
                              granularity: metric.granularity, // Pass granularity for tooltip formatting
                            }}
                            noAccess={Boolean(metric.noAccess)}
                          />
                        );
                      })}
                    </ReactSortable>
                  ) : (
                    // Preview title
                    <div className="dbs-preview-title">{formatMessage({ id: 'dbs_preview' })}</div>
                  )}
                </div>
              </Form.Item>
            </>
          )}

          {/* AI Summary section */}
          {contentType === 'ai-summary' && (
            <>
              <Form.Item>
                <Form>
                  {/* Agent Selection */}
                  <Form.Item label="Agent">
                    <Radio.Group
                      value={agent}
                      onChange={(value) => setAgent(value as 'chatGPT' | 'deepSeek')}
                    >
                      <Radio name="chatGPT">
                        <img src={chatGPTIcon} alt="ChatGPT" />
                        ChatGPT
                      </Radio>
                      <Radio name="deepSeek">
                        <img src={deepSeekIcon} alt="DeepSeek" />
                        DeepSeek
                      </Radio>
                    </Radio.Group>
                  </Form.Item>

                  {/* Prompt Textarea */}
                  <Form.Item label="Prompt" className="dbs-form-item-top18">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder={formatMessage({ id: 'dbs_please_enter' })}
                      className="dbs-prompt-textarea"
                      rows={6}
                    />
                  </Form.Item>

                  {/* Title Input */}
                  {/* <Form.Item label="Title">
                    <Input
                      value={title}
                      onChange={(value) => setTitle(value)}
                    />
                  </Form.Item> */}

                  {/* Summary Text Area */}
                  {/* <Form.Item label={formatMessage({ id: 'dbs_summary' })}>
                    <TinyEditor
                      value={content}
                      getEditorContent={(htmlContent) => setContent(htmlContent)}
                      placeholder={formatMessage({ id: 'dbs_please_enter' })}
                      minHeight={200}
                      maxContentHeight={300}
                      isMaxHeightScroll={true}
                      isShowToolbar={true}
                      className="dbs-tiny-editor"
                    />
                    <div className="dbs-character-count">
                      {getCharacterCount()} {formatMessage({ id: 'dbs_recommended_165_characters' })}
                    </div>
                  </Form.Item> */}

                  {/* Regenerate Button */}
                  {/* <Form.Item>
                    <Button
                      type="weak"
                      onClick={() => console.log('Regenerate Summary')}
                      className="dbs-regenerate-btn"
                    >
                      <Icon type="refresh" />
                      {formatMessage({ id: 'dbs_regenerate' })}
                    </Button>
                  </Form.Item> */}

                  {/* Documents Upload */}
                  <Form.Item label="Documents" className="dbs-form-item-documents">
                    <div>
                      <UploadFileList
                        uniqueId="ai-summary-upload"
                        uploadFolderName="weekly_project_highlight"
                        defaultList={memoizedAiFiles}
                        getIsAllow={handleAiFilesIsAllow}
                        onResult={handleAiFilesChange}
                        multiple={true}
                        maxSize={5 * 1024 * 1024 * 1024} // 5GB
                        buttonTips=""
                        setDeletePathList={setDeletePathList}
                        deletePathList={deletePathList}
                        // fileNameWith={280}
                        // stateWith={180}
                      />
                    </div>
                  </Form.Item>
                </Form>
              </Form.Item>

              {/* Preview Area */}
              <Form.Item>
                <div className="dbs-preview-area">
                  {formatMessage({ id: 'dbs_preview' })}
                </div>
              </Form.Item>
            </>
          )}

          {/* Text content section */}
          {contentType === 'text' && (
            <>
              <Form.Item >
                {/* nested form */}
                <Form>
                  {/* Text Type Selection */}
                  <Form.Item label="Type">
                    <Radio.Group
                      value={textType}
                      onChange={(value) => setTextType(value as 'long' | 'short')}
                    >
                      <Radio name="long">Long Text</Radio>
                      <Radio name="short">Short Text</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {/* Title Input */}
                  <Form.Item label="Title" className="dbs-add-title-input">
                    <Input
                      value={textTitle}
                      onChange={(value) => setTextTitle(value)}
                      placeholder={formatMessage({ id: 'dbs_please_enter' })}
                    />
                  </Form.Item>

                  {/* Text Content with TinyEditor */}
                  <Form.Item label="Text">
                    <TinyEditor
                      value={textContent}
                      getEditorContent={handleTextContentChange}
                      placeholder={formatMessage({ id: 'dbs_please_enter' })}
                      minHeight={200}
                      maxContentHeight={300}
                      isMaxHeightScroll={true}
                      isShowToolbar={true}
                      showCharacterCount={true}
                      recommendedCharacters={recommendedCharacters}
                    // className="dbs-tiny-editor"
                    />
                  </Form.Item>

                  {/* Documents Upload */}
                  <Form.Item label="Documents"  className="dbs-form-item-documents">
                    <div>
                      <UploadFileList
                        uniqueId="add-content-modal-upload"
                        uploadFolderName="weekly_project_highlight"
                        defaultList={memoizedTextFiles}
                        getIsAllow={handleTextFilesIsAllow}
                        onResult={handleTextFilesChange}
                        multiple={true}
                        buttonTips=""
                        setDeletePathList={setDeletePathList}
                        deletePathList={deletePathList}
                        // fileNameWith={280}
                        // stateWith={180}
                      />
                    </div>
                  </Form.Item>
                </Form>
              </Form.Item >

              <Form.Item>
                <div className="dbs-preview-wrapper">
                  {textTitle.trim() || textContent.trim() || textFiles.length > 0 ? (
                    <ContentCard
                      className={`dbs-text-preview-card ${textType === 'short' ? 'dbs-text-preview-card--short' : 'dbs-text-preview-card--long'}`}
                      title={textTitle}
                      type="default"
                      items={[textContent || '']}
                      variant="no-bullet"
                      renderHtml={true}
                      files={previewFiles}
                      showFileNamesOnly
                    />
                  ) : (
                    <div className="dbs-preview-title">{formatMessage({ id: 'dbs_preview' })}</div>
                  )}
                </div>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {contentType === 'ai-summary' ? (
          // AI Summary buttons (Generate)
          <>
            <Button
              type="primary"
              onClick={() => {
                // Handle generate action
                console.log('Generate AI Summary');
              }}
              // disabled={!isAiFilesReady}
              disabled={true}
              className="dbs-gradient-btn"
            >
              {formatMessage({ id: 'dbs_generate' })}
            </Button>
          </>
        ) : (
          // Dashboard and Text content buttons
          <>
            <Button
              type="primary"
              onClick={handleConfirm}
              disabled={contentType === 'text' ? !isTextFilesReady : false}
              className="dbs-gradient-btn"
            >
              {formatMessage({ id: 'dbs_confirm' })}
            </Button>
          </>
        )}
        <Button
          onClick={handleClose}
          // className="dbs-outline-btn"
        >
          {formatMessage({ id: 'dbs_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddNewContentModal;
